import type {
  CodeableConcept,
  Coding,
  Identifier,
  Observation,
  ObservationReferenceRange,
  Patient,
  Practitioner,
  Quantity,
  Reference,
} from '@medplum/fhirtypes'
import { derivedObservationFields } from '#src/observation-logic/derivations/record.ts'
import {
  applyCalculationMethod,
  type DerivationRule,
  derivationInputs,
  observationCategory,
} from '#src/observation-logic/derivations/rules.ts'
import {
  interpretScore,
  type ScoreBand,
} from '#src/observation-logic/derivations/scores.ts'
import type { ConditionEnv } from '#src/observation-logic/evaluate.ts'
import { bandToInterpretation } from '#src/observation-logic/interpretation.ts'
import {
  codedAnswer,
  formatValue,
  numberValue,
  type ObservationValue,
} from '#src/observation-logic/value.ts'
import { OBSERVATION_CALCULATION_EXTENSION_URL } from '#src/structureDefinitions.ts'
import {
  instrumentAnswerOptions,
  instrumentItem,
  instrumentItems,
} from '#src/terminology/instruments/registry.ts'
import type {
  InstrumentItem,
  InstrumentScore,
  InstrumentTemplate,
} from '#src/terminology/instruments/types.ts'
import type { ObservationPanelSummary } from '#src/tiptap/nodes/observationPanelNode.ts'
import { generateUUID, now } from '#src/utils.ts'

// Every Observation one instrument capture writes shares this identifier,
// with the capture's group id as its value.
export const OBSERVATION_GROUP_IDENTIFIER_SYSTEM = 'urn:seen:observation-group'
// One per Observation in a capture (`<group>/panel`, `<group>/item/<code>`,
// `<group>/score/<code>`), so a conditional create makes each once however
// many editors write it.
export const OBSERVATION_GROUP_MEMBER_IDENTIFIER_SYSTEM =
  'urn:seen:observation-group-member'

const UCUM = 'http://unitsofmeasure.org'

export type InstrumentMemberRole = 'panel' | 'item' | 'score'

// Answers by item, keyed `system|code` (see `instrumentAnswerKey`).
export type InstrumentAnswers = Readonly<
  Record<string, ObservationValue | null | undefined>
>

export function instrumentAnswerKey(
  coding: Pick<Coding, 'system' | 'code'>
): string {
  return `${coding.system ?? ''}|${coding.code ?? ''}`
}

export function instrumentMemberKey(
  groupId: string,
  role: InstrumentMemberRole,
  coding?: Pick<Coding, 'code'>
): string {
  return role === 'panel'
    ? `${groupId}/panel`
    : `${groupId}/${role}/${coding?.code ?? ''}`
}

// The query a conditional create uses for one member of a capture, scoped
// to the participant: a group id copied into another note (a template made
// from a captured note) must not find another participant's record.
export function instrumentMemberQuery({
  subject,
  groupId,
  role,
  coding,
}: {
  subject: Reference<Patient>
  groupId: string
  role: InstrumentMemberRole
  coding?: Pick<Coding, 'code'>
}): string {
  return `identifier=${OBSERVATION_GROUP_MEMBER_IDENTIFIER_SYSTEM}|${instrumentMemberKey(groupId, role, coding)}&patient=${subject.reference}&status:not=entered-in-error`
}

// The search for every Observation of the given captures, scoped to the
// participant like `instrumentMemberQuery`: one search however many panels a
// note holds.
export function instrumentGroupQuery({
  subject,
  groupIds,
}: {
  subject: Reference<Patient>
  groupIds: readonly string[]
}) {
  return {
    identifier: groupIds
      .map((groupId) => `${OBSERVATION_GROUP_IDENTIFIER_SYSTEM}|${groupId}`)
      .join(','),
    patient: subject.reference ?? '',
    'status:not': 'entered-in-error',
  }
}

function memberIdentifiers(
  groupId: string,
  role: InstrumentMemberRole,
  coding?: Pick<Coding, 'code'>
): Identifier[] {
  return [
    { system: OBSERVATION_GROUP_IDENTIFIER_SYSTEM, value: groupId },
    {
      system: OBSERVATION_GROUP_MEMBER_IDENTIFIER_SYSTEM,
      value: instrumentMemberKey(groupId, role, coding),
    },
  ]
}

// The capture's group id, read off any of its Observations.
export function observationGroupId(
  observation: Observation
): string | undefined {
  return observation.identifier?.find(
    (identifier) => identifier.system === OBSERVATION_GROUP_IDENTIFIER_SYSTEM
  )?.value
}

export interface InstrumentComputation {
  // Null while the completeness policy is not met.
  value: number | null
  band: ScoreBand | undefined
}

export interface InstrumentComputeOptions {
  // Items hidden by their show-when condition.
  hidden?: readonly Coding[]
  // Reads what a conditional band depends on (education for SLUMS).
  env?: ConditionEnv
}

function answerOptionsFor(template: InstrumentTemplate) {
  return (coding: Coding) => {
    const item = instrumentItem(template, coding)
    return item ? instrumentAnswerOptions(item) : undefined
  }
}

// One score of an instrument from its answers, and its band.
export function computeInstrumentScore(
  template: InstrumentTemplate,
  score: InstrumentScore,
  answers: InstrumentAnswers,
  options: InstrumentComputeOptions
): InstrumentComputation {
  const values = instrumentItems(template).map((item) => ({
    coding: item.coding,
    value: answers[instrumentAnswerKey(item.coding)] ?? null,
  }))
  const result = score.rule.compute(
    derivationInputs(values, {
      variable: options.env?.variable,
      answerOptions: answerOptionsFor(template),
      hidden: options.hidden,
    })
  )
  const value = result?.kind === 'number' ? result.value : null
  return {
    value,
    band:
      value === null
        ? undefined
        : interpretScore(value, score.bands, options.env),
  }
}

// What the panel node shows, and what its HTML is rendered from without a
// lookup: the total, its band, and how many items are answered.
export interface InstrumentSummary {
  total: number | null
  band?: { display: string; severity: ScoreBand['severity']; code: Coding }
  answered: number
  of: number
  scored: boolean
}

export function instrumentSummary(
  template: InstrumentTemplate,
  answers: InstrumentAnswers,
  options: InstrumentComputeOptions
): InstrumentSummary {
  const hidden = options.hidden ?? []
  const visible = instrumentItems(template).filter(
    (item) =>
      !hidden.some(
        (coding) =>
          coding.code === item.coding.code &&
          coding.system === item.coding.system
      )
  )
  const total = template.total
    ? computeInstrumentScore(template, template.total, answers, options)
    : { value: null, band: undefined }
  const summary: InstrumentSummary = {
    total: total.value,
    answered: visible.filter((item) => {
      const value = answers[instrumentAnswerKey(item.coding)]
      return value !== null && value !== undefined
    }).length,
    of: visible.length,
    scored: isScoredTemplate(template),
  }
  if (total.band) {
    summary.band = {
      display: total.band.display,
      severity: total.band.severity,
      code: total.band.code,
    }
  }
  return summary
}

// The panel node's `summary` attribute: the summary plus the title and the
// answered items as rows, so the note's HTML shows the capture without a
// lookup. The node view writes it whenever an answer changes.
export function instrumentPanelSummary(
  template: InstrumentTemplate,
  answers: InstrumentAnswers,
  options: InstrumentComputeOptions
): ObservationPanelSummary {
  const summary = instrumentSummary(template, answers, options)
  const panelSummary: ObservationPanelSummary = {
    title: template.title,
    total: summary.total,
    answered: summary.answered,
    of: summary.of,
    scored: summary.scored,
    rows: instrumentItems(template).flatMap((item) => {
      const value = answers[instrumentAnswerKey(item.coding)]
      return value ? [{ label: item.label, answer: formatValue(value) }] : []
    }),
  }
  if (summary.band) {
    panelSummary.band = {
      display: summary.band.display,
      severity: summary.band.severity,
    }
  }
  return panelSummary
}

function itemValue(value: ObservationValue): Partial<Observation> {
  switch (value.kind) {
    case 'number': {
      if (Number.isInteger(value.value) && !value.unit) {
        return { valueInteger: value.value }
      }
      const valueQuantity: Quantity = { value: value.value }
      if (value.unit) {
        valueQuantity.unit = value.unit
        valueQuantity.system = UCUM
        valueQuantity.code = value.unit
      }
      return { valueQuantity }
    }
    case 'coding': {
      const valueCodeableConcept: CodeableConcept = {
        coding: value.value.map((answer) => codedAnswer(answer)),
      }
      if (value.value.length === 1 && value.value[0]?.display) {
        valueCodeableConcept.text = value.value[0].display
      }
      return { valueCodeableConcept }
    }
    case 'string':
      return { valueString: value.value }
    case 'boolean':
      return { valueBoolean: value.value }
    case 'date':
      return { valueDateTime: value.value }
  }
}

const VALUE_FIELDS = [
  'valueInteger',
  'valueQuantity',
  'valueCodeableConcept',
  'valueString',
  'valueBoolean',
  'valueDateTime',
] as const

function withoutValue(observation: Observation): Observation {
  const next = { ...observation }
  for (const field of VALUE_FIELDS) {
    delete next[field]
  }
  return next
}

export interface InstrumentRecordContext {
  template: InstrumentTemplate
  subject: Reference<Patient>
  performer: Reference<Practitioner>
  status: 'preliminary' | 'final'
  groupId: string
  // When new records were taken: the encounter's start when the note has
  // one, else now.
  effectiveDateTime?: string
}

// A validated tool is a survey; a measurement panel names its own category.
function recordCategory(template: InstrumentTemplate) {
  return observationCategory(template.category ?? 'survey')
}

// Whether a panel scores its answers: a validated tool does, a measurement
// panel only records readings.
export function isScoredTemplate(template: InstrumentTemplate): boolean {
  return (
    !!template.total || instrumentItems(template).some((item) => item.scored)
  )
}

function scoreQuantity(value: number) {
  return { value, unit: '{score}', system: UCUM, code: '{score}' }
}

// One reference range per band of a score, labelled with the band.
export function scoreReferenceRanges(
  score: InstrumentScore
): ObservationReferenceRange[] {
  return score.bands.map((band) => {
    const range: ObservationReferenceRange = {}
    if (band.min !== undefined) {
      range.low = scoreQuantity(band.min)
    }
    if (band.max !== undefined) {
      range.high = scoreQuantity(band.max)
    }
    range.text = band.display
    return range
  })
}

// One item's Observation: the existing one with its new answer, or a new
// one. No answer leaves the record in place without a value.
export function upsertItemObservation({
  template,
  subject,
  performer,
  status,
  groupId,
  effectiveDateTime = now(),
  item,
  value,
  existing,
  calculated,
}: InstrumentRecordContext & {
  item: InstrumentItem
  value: ObservationValue | null
  existing?: Observation
  // The rule that computed the value; absent for an answer given by hand,
  // a calculated item the clinician overrode among them.
  calculated?: DerivationRule
}): Observation {
  const base: Observation = existing
    ? withoutValue(existing)
    : {
        resourceType: 'Observation',
        status,
        identifier: memberIdentifiers(groupId, 'item', item.coding),
        category: recordCategory(template),
        code: { coding: [item.coding], text: item.label },
        subject,
        performer: [performer],
        effectiveDateTime,
      }
  const { method: _method, interpretation: _interpretation, ...plain } = base
  const manual = {
    ...plain,
    extension: base.extension?.filter(
      (extension) => extension.url !== OBSERVATION_CALCULATION_EXTENSION_URL
    ),
  }
  if (!manual.extension?.length) {
    delete manual.extension
  }
  if (!value) {
    return calculated ? base : manual
  }
  if (!calculated) {
    return { ...manual, ...itemValue(value) }
  }
  // A calculated item records how it was worked out and, where its code has
  // published cutoffs, its band, as a computed node's record does.
  const fields = derivedObservationFields({
    rule: calculated,
    value,
    target: item.coding,
    derivedFrom: [],
    existingExtensions: base.extension,
  })
  const record: Observation = {
    ...manual,
    ...itemValue(value),
    method: fields.method,
    extension: fields.extension,
  }
  if (fields.interpretation) {
    record.interpretation = fields.interpretation
  }
  return record
}

// A score's Observation from the answers: value as `{score}`, the items it
// is computed from, its band and its method. Incomplete answers clear the
// value of an existing one.
export function recomputeTotal({
  template,
  subject,
  performer,
  status,
  groupId,
  effectiveDateTime = now(),
  answers,
  itemReferences,
  existing,
  score = template.total,
  options,
}: InstrumentRecordContext & {
  answers: InstrumentAnswers
  // The item Observations the score reads, by answer key.
  itemReferences: Readonly<Record<string, Reference<Observation> | undefined>>
  existing?: Observation
  score?: InstrumentScore
  options: InstrumentComputeOptions
}): Observation | undefined {
  if (!score) {
    return undefined
  }
  const { value, band } = computeInstrumentScore(
    template,
    score,
    answers,
    options
  )
  const derivedFrom = score.rule.inputs.flatMap(
    (coding) => itemReferences[instrumentAnswerKey(coding)] ?? []
  )
  const base: Observation = existing
    ? withoutValue(existing)
    : {
        resourceType: 'Observation',
        status,
        identifier: memberIdentifiers(groupId, 'score', score.coding),
        category: recordCategory(template),
        code: { coding: [score.coding], text: score.label },
        subject,
        performer: [performer],
        effectiveDateTime,
      }
  const ranges = scoreReferenceRanges(score)
  const next: Observation = {
    ...base,
    derivedFrom: derivedFrom.length ? derivedFrom : undefined,
    interpretation: band ? [bandToInterpretation(band)] : undefined,
    referenceRange: ranges.length ? ranges : undefined,
  }
  if (value !== null) {
    next.valueQuantity = scoreQuantity(value)
  }
  return applyCalculationMethod(next, score.rule)
}

// The panel Observation, with every member it has so far.
export function buildPanelObservation({
  template,
  subject,
  performer,
  status,
  groupId,
  effectiveDateTime = now(),
  members,
  existing,
}: InstrumentRecordContext & {
  members: Reference<Observation>[]
  existing?: Observation
}): Observation {
  return {
    ...(existing ?? {
      resourceType: 'Observation',
      status,
      identifier: memberIdentifiers(groupId, 'panel'),
      category: recordCategory(template),
      code: { coding: [template.panel], text: template.title },
      subject,
      performer: [performer],
      effectiveDateTime,
    }),
    hasMember: members.length ? members : undefined,
  }
}

export interface InstrumentObservations {
  panel: { fullUrl: string; resource: Observation }
  items: { fullUrl: string; resource: Observation }[]
  scores: { fullUrl: string; resource: Observation }[]
}

// Every Observation for a complete capture at once, referring to each other
// by `urn:uuid` full URLs, for one transaction bundle.
export function buildInstrumentObservations({
  template,
  answers,
  subject,
  performer,
  status,
  groupId,
  effectiveDateTime = now(),
  hidden,
  env,
  calculatedRule,
}: InstrumentRecordContext & {
  answers: InstrumentAnswers
  // The rule that computed an item, for the panel's calculated items not
  // overridden by hand.
  calculatedRule?: (item: InstrumentItem) => DerivationRule | undefined
  hidden?: readonly Coding[]
  env?: ConditionEnv
}): InstrumentObservations {
  const context = {
    template,
    subject,
    performer,
    status,
    groupId,
    effectiveDateTime,
  }
  const items = instrumentItems(template).flatMap((item) => {
    const value = answers[instrumentAnswerKey(item.coding)]
    return value
      ? [
          {
            key: instrumentAnswerKey(item.coding),
            fullUrl: `urn:uuid:${generateUUID()}`,
            resource: upsertItemObservation({
              ...context,
              item,
              value,
              calculated: calculatedRule?.(item),
            }),
          },
        ]
      : []
  })
  const itemReferences = Object.fromEntries(
    items.map((item) => [item.key, { reference: item.fullUrl }])
  )
  const scores = [template.total, ...(template.subtotals ?? [])].flatMap(
    (score) => {
      const resource = score
        ? recomputeTotal({
            ...context,
            answers,
            itemReferences,
            score,
            options: { hidden, env },
          })
        : undefined
      return resource
        ? [{ fullUrl: `urn:uuid:${generateUUID()}`, resource }]
        : []
    }
  )
  const panelUrl = `urn:uuid:${generateUUID()}`
  return {
    panel: {
      fullUrl: panelUrl,
      resource: buildPanelObservation({
        ...context,
        members: [...items, ...scores].map((member) => ({
          reference: member.fullUrl,
        })),
      }),
    },
    items: items.map(({ fullUrl, resource }) => ({ fullUrl, resource })),
    scores,
  }
}

function observationValue(observation: Observation): ObservationValue | null {
  if (observation.valueInteger !== undefined) {
    return { kind: 'number', value: observation.valueInteger }
  }
  if (observation.valueQuantity?.value !== undefined) {
    return numberValue(
      observation.valueQuantity.value,
      observation.valueQuantity.code ?? observation.valueQuantity.unit
    )
  }
  const coding = observation.valueCodeableConcept?.coding?.filter(
    (answer): answer is Coding & { code: string } => !!answer.code
  )
  if (coding?.length) {
    return {
      kind: 'coding',
      value: coding.map((answer) => codedAnswer(answer)),
    }
  }
  if (observation.valueString !== undefined) {
    return { kind: 'string', value: observation.valueString }
  }
  if (observation.valueBoolean !== undefined) {
    return { kind: 'boolean', value: observation.valueBoolean }
  }
  if (observation.valueDateTime) {
    return { kind: 'date', value: observation.valueDateTime }
  }
  return null
}

interface ReadAnswers {
  [key: string]: ObservationValue | null
}

interface ReadInstrumentObservations {
  answers: ReadAnswers
  total: ObservationValue | null
}

// The answers a capture recorded, read back from its members by the
// template's item codes. Members entered in error are ignored.
export function fromInstrumentObservations(
  template: InstrumentTemplate,
  members: readonly Observation[]
): ReadInstrumentObservations {
  const answers: ReadAnswers = {}
  let total: ObservationValue | null = null
  for (const member of members) {
    if (member.status === 'entered-in-error') {
      continue
    }
    const coding = member.code.coding?.[0]
    if (!coding) {
      continue
    }
    const item = instrumentItem(template, coding)
    if (item) {
      answers[instrumentAnswerKey(item.coding)] = observationValue(member)
    } else if (
      template.total &&
      coding.code === template.total.coding.code &&
      coding.system === template.total.coding.system
    ) {
      total = observationValue(member)
    }
  }
  return { answers, total }
}
